import { useTranslation } from "react-i18next";

import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import type { ToolAudience } from "@/lib/api";
import { CHIP, CHIP_FOCUS, CHIP_TONE } from "@/lib/chips";
import { ROW_CLICK_THROUGH } from "@/lib/row-click";
import { cn } from "@/lib/utils";

/**
 * Each level's colours, from light to dark along the ladder, in De Ceuster's
 * blues only: everyone in the Lezen chip's light tint, a team in that tint
 * with a thin dc-blauw-licht line (inset, so the badge keeps the size of the
 * others), the admins in dc-blauw-licht, the owner in dc-blauw-midden and the
 * developers in solid De Ceuster blauw, as the Schrijven chip.
 */
const AUDIENCE_TONE: Record<ToolAudience, string> = {
  everyone: CHIP_TONE.read,
  team: cn(CHIP_TONE.read, "ring-highlight ring-1 ring-inset"),
  admin: "bg-highlight text-highlight-foreground",
  owner: "bg-primary-mid text-primary-foreground",
  developer: CHIP_TONE.write,
};

interface AudienceProps {
  /** Who the tool is for, one level of the ladder the overview gives the admin roles. */
  audience: ToolAudience;
}

/**
 * Who a tool is for, in the admin roles' "Voor" column: one badge in the
 * shape of the dashboard's chips. Each level includes everyone above it on
 * the ladder (developers, the owner, the admins, a team, everyone), and says
 * who that is on hover and on keyboard focus ("Beheerders, de eigenaar en de
 * ontwikkelaars"). A team is never named: the Teams tab of Mensen en teams
 * says which team gets which group. A tooltip never opens on touch, so a click
 * on the badge opens the tool's dialog, as a click on its row does
 * (`ROW_CLICK_THROUGH`), where `AudienceFact` writes it out. The badge keeps
 * its full colours in a greyed row: it says who the tool is for, whether the
 * viewer gets it now or not.
 */
export function AudienceBadge({ audience }: AudienceProps) {
  const { t } = useTranslation();
  const name = t(`tools.audience.${audience}`);
  // "Ontwikkelaars" is abbreviated in the column, so a developer's title lines fit at 800 px;
  // screen readers and the dialog keep the full name.
  const shown = audience === "developer" ? t("tools.audienceShort.developer") : name;
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span
          tabIndex={0}
          data-audience={audience}
          aria-label={shown === name ? undefined : name}
          {...ROW_CLICK_THROUGH}
          className={cn(CHIP, AUDIENCE_TONE[audience], CHIP_FOCUS)}
        >
          {shown}
        </span>
      </TooltipTrigger>
      <TooltipContent>{t(`tools.audienceHint.${audience}`)}</TooltipContent>
    </Tooltip>
  );
}

/**
 * Who a tool is for, in its dialog: the badge, and who that is written out
 * after it in muted text, so it reads on a phone and in a screen reader's
 * browse mode too, where no tooltip opens.
 */
export function AudienceFact({ audience }: AudienceProps) {
  const { t } = useTranslation();
  return (
    <span className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
      <span data-audience={audience} className={cn(CHIP, AUDIENCE_TONE[audience])}>
        {t(`tools.audience.${audience}`)}
      </span>
      <span className="text-muted">{t(`tools.audienceHint.${audience}`)}</span>
    </span>
  );
}
